/**
 * ➕ [Phase 56.5 — 2026-07-09] Dialog "Insérer un bon de travail non cédulé"
 * extrait depuis CalendrierProduction.jsx (bloc legacy ~L2081-2149).
 *
 * Rôle : quand l'utilisatrice double-clique sur une case vide du calendrier,
 * on lui propose la liste des BT sans date pour en insérer un.
 *
 * Contrat :
 *   - `data` : payload showInsertBT = { date: 'YYYY-MM-DD' } OU null
 *   - `search` / `onSearchChange(v)` : recherche client/projet
 *   - `loading` : booléen loadingInsert
 *   - `bons` : liste unscheduledBons
 *   - `employees` : liste des employés (pour retrouver la couleur chef)
 *   - `onCancel()` : ferme sans insérer
 *   - `onInsert(bon, date)` : insère le BT choisi à la date cible
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { formatLocalTime } from '@/utils/formatLocalTime';

export default function InsertBTDialog({
  data, search, onSearchChange, loading, bons = [], employees = [], onCancel, onInsert,
}) {
  if (!data) return null;
  const q = (search || '').trim().toLowerCase();
  const filtered = q
    ? bons.filter(b =>
      (b.client_nom || '').toLowerCase().includes(q) ||
      (b.nom_dossier || '').toLowerCase().includes(q) ||
      (b.chef_equipe_nom || '').toLowerCase().includes(q),
    )
    : bons;

  return (
    <Dialog open={!!data} onOpenChange={() => onCancel?.()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-blue-600" />
            Inserer un bon de travail
          </DialogTitle>
          <DialogDescription>
            Cedule a partir du {data.date ? formatLocalTime(data.date + 'T12:00:00', { dateOnly: true, weekday: 'long', withoutYear: true }) : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <Input
            placeholder="Rechercher un client ou un projet..."
            value={search || ''}
            onChange={e => onSearchChange?.(e.target.value)}
            data-testid="search-bt-insert"
          />
          {loading ? (
            <div className="text-center py-8 text-slate-400 text-sm">Chargement des bons...</div>
          ) : bons.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Aucun bon de travail sans date</div>
          ) : (
            <div className="space-y-1.5">
              {filtered.map(bon => {
                const chef = employees.find(e => e.id === bon.chef_equipe_id);
                return (
                  <div
                    key={bon.id}
                    className="border rounded-lg p-3 hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-colors"
                    onClick={() => onInsert?.(bon, data.date)}
                    data-testid={`insert-bt-${bon.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{bon.client_nom || 'Sans client'}</p>
                        <p className="text-xs text-slate-500 truncate">{bon.nom_dossier || 'Projet'} | {bon.departement || 'PRQC'}</p>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        {chef && (
                          <div className="flex items-center gap-1">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: chef.couleur_equipe || '#3B82F6' }} />
                            <span className="text-xs text-slate-600">{bon.chef_equipe_nom}</span>
                          </div>
                        )}
                        {!bon.chef_equipe_id && <span className="text-xs text-amber-600">Sans equipe</span>}
                        <p className="text-xs text-slate-400">{bon.type_travaux === 'interieur' ? '☁️ Int.' : '☀️ Ext.'} | ~{bon.heures_budgetees || 0}h</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
